import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { CircularProgress, Dialog, DialogContent } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useAddOrderItemsFromPartLibraryMutation } from '@/app/services/orderItems'
import { useCreateOrderMutation } from '@/app/services/orders'
import { selectContactId } from '@/app/slices/contactsSlice'
import { selectCustomerId } from '@/app/slices/customerSlice'
import { selectGuestUser, selectIsGuestLoggedIn } from '@/app/slices/guestUserSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import PartLibraryDialog from '@/common/components/PartLibraryDialog/PartLibraryDialog'
import { ORDER_USER_TYPE } from '@/common/utils'

const classes = {
    dialogContent: {
        display: 'flex',
        flexDirection: 'row',
        p: 0,
    },
}

const AddFromPartLibrary = ({ onClose, open }) => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { orderId: orderIdFromPath } = useParams()
    const { isAuthenticated } = useKindeAuth()

    const organisationId = useSelector(selectOrganisationId)
    const currentCustomerId = useSelector(selectCustomerId)
    const currentContactId = useSelector(selectContactId)
    const isGuestLoggedIn = useSelector(selectIsGuestLoggedIn)
    const guestUser = useSelector(selectGuestUser)

    const [createOrder, { isLoading: isCreating, reset: resetCreateOrder }] = useCreateOrderMutation()
    const [addOrderItemsFromPartLibrary, { isLoading: isUploading, reset: resetUploadOrderItems }] =
        useAddOrderItemsFromPartLibraryMutation()

    const [isInitializing, setIsInitializing] = useState(false)
    const [orderId, setOrderId] = useState(orderIdFromPath)

    const initializeOrder = async () => {
        if (!orderId && !isInitializing) {
            setIsInitializing(true)
            const userType = isAuthenticated ? ORDER_USER_TYPE.Customer : ORDER_USER_TYPE.Guest
            const contactId = isAuthenticated ? currentContactId : isGuestLoggedIn ? guestUser.contactId : null
            const customerId = isAuthenticated ? currentCustomerId : isGuestLoggedIn ? guestUser.customerId : null

            try {
                const createOrderResponse = await createOrder({
                    organisationId,
                    payload: {
                        organisationId,
                        quoteUserType: userType,
                        customerContactId: contactId,
                        customerId: customerId,
                    },
                }).unwrap()
                setOrderId(createOrderResponse.id)
            } catch (e) {
                enqueueSnackbar(t('Failed to create order'), {
                    variant: 'error',
                })
                handleClose()
            } finally {
                setIsInitializing(false)
            }
        }
    }

    const handleClose = () => {
        onClose()
    }

    const handleAddPartLibraryParts = async (orderItemsToCreate) => {
        const partsToAdd = []
        orderItemsToCreate.forEach((partLibraryItem) => {
            const newPart = {
                partLibraryId: partLibraryItem.partLibraryId,
                //partSource: 'partLibrary',
                quantity: partLibraryItem.newQuantity ?? 1,
                //partId: partLibraryItem.partId,
            }
            partsToAdd.push(newPart)
        })

        try {
            await addOrderItemsFromPartLibrary({
                organisationId,
                orderId: orderId,
                partsToAdd,
            }).unwrap()
            enqueueSnackbar(`${t(`${orderItemsToCreate.length} parts added to order`)}`, { variant: 'success' })
        } catch (error) {
            console.error(error)
            enqueueSnackbar(`${t('An error occurred, please try again.')}`, {
                variant: 'error',
                autoHideDuration: 5000,
            })
        } finally {
            handleClose()
        }

        if (!orderIdFromPath) {
            navigate(`orders/${orderId}`)
        }
    }

    if (open && !orderId && !isInitializing) {
        initializeOrder()
    }

    return (
        <Dialog
            fullScreen={true}
            open={open}
            sx={classes.dialogRoot}
            onClose={handleClose}
        >
            {' '}
            {orderId && !isInitializing ? (
                <DialogContent sx={classes.dialogContent}>
                    <PartLibraryDialog
                        disabledButtons={isCreating || isUploading}
                        handlePartsLibrarySubmit={handleAddPartLibraryParts}
                        onCancelClicked={handleClose}
                    />
                </DialogContent>
            ) : (
                <DialogContent sx={classes.dialogContent}>
                    <CircularProgress />
                </DialogContent>
            )}
        </Dialog>
    )
}

AddFromPartLibrary.propTypes = {
    customer: PropTypes.object,
    open: PropTypes.bool,
    onClose: PropTypes.func,
}

export default AddFromPartLibrary
